import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import DateSelector from '@/components/selectors/DateSelector';

interface DateRangeSelectorProps {
    startDate: string | DateTime;
    endDate: string | DateTime;
    onStartDateChange: (date: string) => void;
    onEndDateChange: (date: string) => void;
    maxRange?: number;
    locale?: string;
    error?: boolean;
    helperText?: string;
}

const DateRangeSelector = ({
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    helperText
}: DateRangeSelectorProps) => {
    const { t } = useTranslation();

    const startDateStr = useMemo(() => {
        if (typeof startDate === 'string') return startDate;
        return startDate?.toISODate() || '';
    }, [startDate]);

    const endDateStr = useMemo(() => {
        if (typeof endDate === 'string') return endDate;
        return endDate?.toISODate() || '';
    }, [endDate]);

    const startDateTime = useMemo(() => DateTime.fromISO(startDateStr), [startDateStr]);

    const endDateTime = useMemo(() => DateTime.fromISO(endDateStr), [endDateStr]);

    const today = DateTime.local();

    return (
        <>
            <Box className="filter-field" sx={{ width: 200, flex: '0 0 auto' }}>
                <DateSelector
                    value={startDateStr || null}
                    onChange={onStartDateChange}
                    label={t('from')}
                    maxDate={endDateTime.isValid ? endDateTime : today}
                />
                {helperText && (
                    <Typography variant="caption" sx={{ display: 'block', mt: '4px', color: '#f44336 !important' }}>
                        {helperText}
                    </Typography>
                )}
            </Box>

            <Box className="filter-field" sx={{ width: 200, flex: '0 0 auto' }}>
                <DateSelector
                    value={endDateStr || null}
                    onChange={onEndDateChange}
                    label={t('until')}
                    minDate={startDateTime.isValid ? startDateTime : undefined}
                    maxDate={today}
                />
                {helperText && (
                    <Typography variant="caption" sx={{ display: 'block', mt: '4px', color: '#f44336 !important' }}>
                        {helperText}
                    </Typography>
                )}
            </Box>
        </>
    );
};

export default DateRangeSelector;
